import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Rellena todos los campos");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data.received) {
        setSent(true);
        toast.success("Mensaje enviado. Te respondemos en menos de 24 h.");
        setForm({ name: "", email: "", message: "" });
      } else {
        toast.error("No se pudo enviar. Escríbenos directamente a info@autonomopro.online");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-600 grid place-items-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-white" />
        </div>
        <h3 className="font-display text-xl font-bold text-slate-900 mt-4">
          ¡Mensaje enviado!
        </h3>
        <p className="text-sm text-slate-600 mt-2">
          Te contestamos personalmente en menos de 24 h a tu email.
        </p>
        <Button
          variant="ghost"
          onClick={() => setSent(false)}
          className="mt-4 text-emerald-700"
          data-testid="contact-form-reset"
        >
          Enviar otro mensaje
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4" data-testid="contact-form">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-5 h-5 text-blue-600" />
        <h3 className="font-display font-bold text-lg text-slate-900">
          Envíanos un mensaje
        </h3>
      </div>
      <div>
        <Label htmlFor="contact-name">Nombre</Label>
        <Input
          id="contact-name"
          value={form.name}
          onChange={set("name")}
          placeholder="María López"
          required
          data-testid="contact-name-input"
          className="mt-1.5"
        />
      </div>
      <div>
        <Label htmlFor="contact-email">Tu email</Label>
        <Input
          id="contact-email"
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="tucorreo@ejemplo.com"
          required
          data-testid="contact-email-input"
          className="mt-1.5"
        />
      </div>
      <div>
        <Label htmlFor="contact-message">Mensaje</Label>
        <Textarea
          id="contact-message"
          value={form.message}
          onChange={set("message")}
          placeholder="Cuéntanos qué necesitas..."
          rows={4}
          required
          data-testid="contact-message-input"
          className="mt-1.5"
        />
      </div>
      <Button
        type="submit"
        disabled={sending}
        className="w-full bg-blue-600 hover:bg-blue-700 h-11"
        data-testid="contact-submit-btn"
      >
        {sending ? (
          "Enviando..."
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" /> Enviar mensaje
          </>
        )}
      </Button>
      <div className="text-xs text-slate-500 text-center">
        Al enviar, aceptas que te contactemos en el email indicado.
      </div>
    </form>
  );
}
